import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AppButton from "../button/AppButton";
import AppDateInput from "../inputs/AppDateInput";
import AppTextInput from "../inputs/AppTextInput";
import AppPhoneInput from "../inputs/AppPhoneInput";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { CreateBookingRequest } from "../../models/RequestModels/CreateBookingRequest";
import { UpdateBookingRequest } from "../../models/RequestModels/UpdateBookingRequest";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { useTranslation } from "../../translations/i18n";

interface BookingFormModalProps {
  open: boolean;
  loading: boolean;
  units: UnitResponse[];
  booking?: BookingResponse | null;
  defaultUnitId?: number | null;
  defaultCheckIn?: string;
  defaultCheckOut?: string;
  defaultCurrency: string;
  onClose: () => void;
  onSubmit: (body: CreateBookingRequest | UpdateBookingRequest) => void;
}

const emptyToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

const getTomorrow = (date: string): string => {
  const parsed = new Date(`${date}T00:00:00`);
  parsed.setDate(parsed.getDate() + 1);
  return parsed.toISOString().split("T")[0];
};

const getNightCount = (checkIn: string, checkOut: string) => {
  const start = new Date(`${checkIn}T00:00:00`);
  const end = new Date(`${checkOut}T00:00:00`);
  const diff = Math.ceil((end.getTime() - start.getTime()) / 86400000);

  return diff > 0 ? diff : 0;
};

const BookingFormModal = ({
  open,
  loading,
  units,
  booking,
  defaultUnitId,
  defaultCheckIn,
  defaultCheckOut,
  defaultCurrency,
  onClose,
  onSubmit,
}: BookingFormModalProps) => {
  const { t } = useTranslation();

  const today = useMemo(() => new Date().toISOString().split("T")[0], []);

  const [unitId, setUnitId] = useState<number>(defaultUnitId || 0);
  const [guestFirstName, setGuestFirstName] = useState("");
  const [guestLastName, setGuestLastName] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestCount, setGuestCount] = useState("");
  const [checkIn, setCheckIn] = useState(defaultCheckIn || today);
  const [checkOut, setCheckOut] = useState(
    defaultCheckOut || getTomorrow(today),
  );
  const [totalPrice, setTotalPrice] = useState("");
  const [currency, setCurrency] = useState(defaultCurrency);
  const [paymentStatus, setPaymentStatus] =
    useState<BookingResponse["payment_status"]>("unpaid");
  const [notes, setNotes] = useState("");

  const selectedUnit = units.find((unit) => unit.id === unitId);

  const nights = useMemo(() => {
    return getNightCount(checkIn, checkOut);
  }, [checkIn, checkOut]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextCheckIn = booking?.check_in || defaultCheckIn || today;
    const nextCheckOut =
      booking?.check_out ||
      defaultCheckOut ||
      getTomorrow(defaultCheckIn || today);

    setUnitId(booking?.unit_id || defaultUnitId || units[0]?.id || 0);
    setGuestFirstName(booking?.guest?.first_name || "");
    setGuestLastName(booking?.guest?.last_name || "");
    setGuestPhone(booking?.guest?.phone || "");
    setGuestEmail(booking?.guest?.email || "");
    setGuestCount(booking?.guest_count ? String(booking.guest_count) : "");
    setCheckIn(nextCheckIn);
    setCheckOut(nextCheckOut);
    setCurrency(booking?.currency || defaultCurrency);
    setPaymentStatus(booking?.payment_status || "unpaid");
    setNotes(booking?.notes || "");

    if (booking?.total_price) {
      setTotalPrice(String(Number(booking.total_price)));
      return;
    }

    const currentUnit =
      units.find((unit) => unit.id === (defaultUnitId || units[0]?.id)) || null;

    const defaultPrice = Number(currentUnit?.default_price_per_night || 0);
    const defaultNights = getNightCount(nextCheckIn, nextCheckOut);

    setTotalPrice(
      defaultPrice > 0 ? String(defaultPrice * Math.max(defaultNights, 1)) : "",
    );
  }, [
    open,
    booking,
    defaultUnitId,
    defaultCheckIn,
    defaultCheckOut,
    defaultCurrency,
    today,
    units,
  ]);

  useEffect(() => {
    if (booking || totalPrice || !selectedUnit?.default_price_per_night) {
      return;
    }

    const defaultPrice = Number(selectedUnit.default_price_per_night);

    if (!Number.isNaN(defaultPrice) && defaultPrice > 0) {
      setTotalPrice(String(defaultPrice * Math.max(nights, 1)));
    }
  }, [booking, selectedUnit, totalPrice, nights]);

  const parsedTotalPrice = Number(totalPrice || 0);
  const pricePerNight =
    nights > 0 && !Number.isNaN(parsedTotalPrice)
      ? parsedTotalPrice / nights
      : 0;

  const isValid =
    unitId > 0 &&
    guestFirstName.trim().length > 0 &&
    checkIn < checkOut &&
    nights > 0 &&
    parsedTotalPrice >= 0;

  const handleSubmit = () => {
    if (!isValid) {
      return;
    }

    onSubmit({
      unit_id: unitId,
      guest_first_name: guestFirstName.trim(),
      guest_last_name: emptyToNull(guestLastName),
      guest_phone: emptyToNull(guestPhone),
      guest_email: emptyToNull(guestEmail),
      guest_count: guestCount ? Number(guestCount) : null,
      check_in: checkIn,
      check_out: checkOut,
      price_per_night: Number(pricePerNight.toFixed(2)),
      currency,
      payment_status: paymentStatus,
      source: booking?.source || "manual",
      source_reference: booking?.source_reference || null,
      notes: emptyToNull(notes),
    });
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>
        {booking ? t("booking.bookingDetails") : t("booking.newBooking")}
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>{t("booking.unit")}</InputLabel>
            <Select
              label={t("booking.unit")}
              value={unitId || ""}
              disabled={loading}
              onChange={(event) => setUnitId(Number(event.target.value))}
            >
              {units.map((unit) => (
                <MenuItem key={unit.id} value={unit.id}>
                  {unit.property?.name
                    ? `${unit.property.name} · ${unit.name}`
                    : unit.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <AppTextInput
              label={t("booking.guestFirstName")}
              value={guestFirstName}
              onChange={(event) => setGuestFirstName(event.target.value)}
              disabled={loading}
              required
            />

            <AppTextInput
              label={t("booking.guestLastName")}
              value={guestLastName}
              onChange={(event) => setGuestLastName(event.target.value)}
              disabled={loading}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <AppPhoneInput
              label={t("booking.phone")}
              value={guestPhone}
              onChange={setGuestPhone}
              placeholder={t("booking.phonePlaceholder")}
              disabled={loading}
            />

            <AppTextInput
              label={t("booking.email")}
              type="email"
              value={guestEmail}
              onChange={(event) => setGuestEmail(event.target.value)}
              disabled={loading}
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <AppDateInput
              label={t("booking.checkIn")}
              value={checkIn}
              onChange={(event) => setCheckIn(event.target.value)}
              disabled={loading}
              required
            />

            <AppDateInput
              label={t("booking.checkOut")}
              value={checkOut}
              onChange={(event) => setCheckOut(event.target.value)}
              disabled={loading}
              required
            />
          </Stack>

          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <AppTextInput
              label={t("booking.total")}
              type="number"
              value={totalPrice}
              onChange={(event) => setTotalPrice(event.target.value)}
              disabled={loading}
              required
            />

            <AppTextInput
              label={t("booking.currency")}
              value={currency}
              onChange={(event) =>
                setCurrency(event.target.value.toUpperCase())
              }
              disabled={loading}
              required
            />

            <AppTextInput
              label={t("booking.numberOfGuests")}
              type="number"
              value={guestCount}
              onChange={(event) => setGuestCount(event.target.value)}
              disabled={loading}
            />
          </Stack>

          <FormControl fullWidth>
            <InputLabel>{t("booking.paymentStatus")}</InputLabel>
            <Select
              label={t("booking.paymentStatus")}
              value={paymentStatus}
              disabled={loading}
              onChange={(event) =>
                setPaymentStatus(
                  event.target.value as BookingResponse["payment_status"],
                )
              }
            >
              <MenuItem value="unpaid">{t("booking.unpaid")}</MenuItem>
              <MenuItem value="deposit">{t("booking.deposit")}</MenuItem>
              <MenuItem value="paid">{t("booking.paid")}</MenuItem>
            </Select>
          </FormControl>

          <AppTextInput
            label={t("booking.notes")}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={loading}
            multiline
            minRows={3}
          />

          <Typography color={checkIn < checkOut ? "text.secondary" : "error"}>
            {checkIn < checkOut
              ? `${t("booking.nights")}: ${nights} · ${t(
                  "booking.pricePerNight",
                )}: ${pricePerNight.toFixed(2)} ${currency}`
              : t("booking.checkoutAfterCheckin")}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <AppButton
          title={t("common.cancel")}
          variant="outlined"
          color="inherit"
          fullWidth={false}
          disabled={loading}
          onClick={handleClose}
        />

        <AppButton
          title={booking ? t("common.update") : t("booking.createBooking")}
          fullWidth={false}
          loading={loading}
          disabled={!isValid}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default BookingFormModal;
